import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { Wand2, FileText, Loader2 } from 'lucide-react'
import {
  ChatInput,
  ChatMessages,
  SpecsPreviewModal,
  ScopingHeader,
  SPEC_SECTION_KEYS,
  type ChatMessageData,
  type SpecSection,
  type SectionStatus,
} from '../components'
import { useProjectByShortId, useScopingAIAssistant } from '@/features/team/hooks'
import { ConfirmModal } from '@/shared/components/confirm-modal'
import { cn } from '@/shared/lib/utils'
import { supabase } from '@/shared/lib/supabase'

const MAX_ENHANCEMENTS = 3

/**
 * Scoping V2 Page - Chat interface with NicolAI
 * Design reference: Pencil Node ID ty5NQ
 */
export function ScopingV2Page() {
  const { t, i18n } = useTranslation()
  const navigate = useLocalizedNavigate()
  const queryClient = useQueryClient()
  const { shortId } = useParams<{ shortId: string }>()

  // Fetch project data
  const { data: project, isLoading: isLoadingProject, error: projectError } = useProjectByShortId(shortId)

  // Local state for specs content with ref to handle rapid updates
  const [specsContent, setSpecsContent] = useState('')
  const specsContentRef = useRef('')

  // Sync specs content when project loads
  useEffect(() => {
    if (project?.specs_content) {
      setSpecsContent(project.specs_content)
      specsContentRef.current = project.specs_content
    }
  }, [project?.specs_content])

  // Build project context for AI assistant
  const projectContext = useMemo(() => {
    if (!project) return undefined
    return {
      name: project.name,
      description: project.description,
      objective: project.objective,
      targetUsers: project.target_users,
      existingAssets: project.existing_assets,
      constraints: project.constraints,
      inspirations: project.inspirations,
    }
  }, [project])

  // Merge table content intelligently (keyed by first column: F1, F2, etc.)
  const mergeTableContent = useCallback((existingContent: string, newContent: string): string => {
    // Extract table structure from markdown
    const extractTable = (content: string): {
      headers: string | null
      separator: string | null
      rows: Map<string, string>
      rowOrder: string[]
    } => {
      const lines = content.split('\n')
      let headers: string | null = null
      let separator: string | null = null
      const rows = new Map<string, string>()
      const rowOrder: string[] = []

      for (const line of lines) {
        const trimmed = line.trim()
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
          if (!headers) {
            headers = trimmed
          } else if (trimmed.match(/^\|[\s\-:|]+\|$/)) {
            separator = trimmed
          } else if (separator) {
            const keyMatch = trimmed.match(/^\|\s*\*?\*?([^|*]+?)\*?\*?\s*\|/)
            if (keyMatch) {
              const key = keyMatch[1].trim().toLowerCase()
              if (!rows.has(key)) {
                rows.set(key, trimmed)
                rowOrder.push(key)
              }
            }
          }
        }
      }

      return { headers, separator, rows, rowOrder }
    }

    const existing = extractTable(existingContent)
    const newTable = extractTable(newContent)

    // If both have tables, merge by key
    if (existing.headers && existing.separator && newTable.headers) {
      const mergedRows = new Map(existing.rows)
      const mergedOrder = [...existing.rowOrder]

      for (const [key, row] of newTable.rows) {
        if (mergedRows.has(key)) {
          mergedRows.set(key, row)
        } else {
          mergedRows.set(key, row)
          mergedOrder.push(key)
        }
      }

      mergedOrder.sort((a, b) => {
        const numA = parseInt(a.replace(/\D/g, '')) || 999
        const numB = parseInt(b.replace(/\D/g, '')) || 999
        return numA - numB
      })

      const tableRows = mergedOrder.map(key => mergedRows.get(key)!).join('\n')
      const mergedTable = `${existing.headers}\n${existing.separator}\n${tableRows}\n`

      const tableRegex = /\|[^\n]+\|\n\|[\s\-:|]+\|\n(?:\|[^\n]+\|\n?)*/
      if (existingContent.match(tableRegex)) {
        return existingContent.replace(tableRegex, mergedTable)
      }

      return mergedTable
    }

    // Check if new content has subsections
    const hasSubsections = newContent.match(/^###\s+/m)

    if (hasSubsections) {
      const existingSubsections = new Map<string, string>()
      const subsectionRegex = /###\s+([^\n]+)\n([\s\S]*?)(?=###\s+|$)/g
      let match

      while ((match = subsectionRegex.exec(existingContent)) !== null) {
        existingSubsections.set(match[1].trim().toLowerCase(), match[0])
      }

      let mergedContent = existingContent
      subsectionRegex.lastIndex = 0

      while ((match = subsectionRegex.exec(newContent)) !== null) {
        const subsectionTitle = match[1].trim()
        const subsectionKey = subsectionTitle.toLowerCase()
        const newSubsectionContent = match[0]

        if (existingSubsections.has(subsectionKey)) {
          const oldSubsection = existingSubsections.get(subsectionKey)!
          mergedContent = mergedContent.replace(oldSubsection, newSubsectionContent)
        } else {
          mergedContent = mergedContent.trim() + '\n\n' + newSubsectionContent
        }
      }

      return mergedContent
    }

    // Fallback: append new content with separator
    return existingContent.trim() + '\n\n' + newContent
  }, [])

  // Handle specs update from AI assistant
  const handleSpecsUpdate = useCallback(
    async (update: { section: string; content: string }) => {
      if (!project?.id) return

      try {
        let updatedContent: string

        // If section is "full", replace entire specs content (initial draft)
        if (update.section === 'full') {
          updatedContent = update.content
        } else {
          // Map section names to document structure (NicolAI v3 - 9 sections)
          // Keys are snake_case (from Edge Function) → values are i18n keys (camelCase)
          const sectionTitles: Record<string, string> = {
            summary: t('scoping.sections.summary'),
            context: t('scoping.sections.context'),
            users: t('scoping.sections.users'),
            features: t('scoping.sections.features'),
            workflows: t('scoping.sections.workflows'),
            architecture: t('scoping.sections.architecture'),
            constraints: t('scoping.sections.constraints'),
            risks: t('scoping.sections.risks'),
            success_criteria: t('scoping.sections.successCriteria'), // snake_case → camelCase
          }

          const sectionTitle = sectionTitles[update.section] || update.section

          // Use ref to get the LATEST content (fixes race condition with multiple rapid updates)
          updatedContent = specsContentRef.current

          // Check if section already exists
          const sectionRegex = new RegExp(
            `## ${sectionTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[\\s\\S]*?(?=\\n## |$)`,
            'i'
          )
          const sectionMatch = updatedContent.match(sectionRegex)

          if (sectionMatch) {
            // Section exists - merge content intelligently
            const existingSectionContent = sectionMatch[0]
            const existingBody = existingSectionContent.replace(/^##[^\n]+\n+/, '').trim()
            const mergedBody = mergeTableContent(existingBody, update.content)

            updatedContent = updatedContent.replace(
              sectionRegex,
              `## ${sectionTitle}\n\n${mergedBody}\n\n`
            )
          } else {
            // Append new section
            updatedContent = updatedContent.trim()
            if (updatedContent) {
              updatedContent += '\n\n'
            }
            updatedContent += `## ${sectionTitle}\n\n${update.content}\n`
          }
        }

        // Update ref SYNCHRONOUSLY so next rapid call sees the latest value
        specsContentRef.current = updatedContent

        // Update React state for UI
        setSpecsContent(updatedContent)

        // Persist to database
        const { error } = await supabase
          .from('projects')
          .update({
            specs_content: updatedContent,
            specs_updated_at: new Date().toISOString(),
          })
          .eq('id', project.id)

        if (error) {
          console.error('Error updating specs:', error)
          toast.error(t('scopingV2.ai.specsUpdateError'))
        } else {
          // Only show toast for partial updates, not initial draft
          if (update.section !== 'full') {
            toast.success(t('scopingV2.ai.specsUpdated'))
          }
          // Invalidate project query to sync data
          queryClient.invalidateQueries({ queryKey: ['project', 'short', shortId] })
        }
      } catch (err) {
        console.error('Error updating specs:', err)
        toast.error(t('scopingV2.ai.specsUpdateError'))
      }
    },
    [project?.id, shortId, t, mergeTableContent, queryClient]
  )

  // AI Assistant hook - connected to real NicolAI backend
  const {
    messages: aiMessages,
    isLoading: isAiLoading,
    error: aiError,
    sendMessage,
  } = useScopingAIAssistant(
    project?.id,
    projectContext,
    specsContent,
    { onSpecsUpdate: handleSpecsUpdate }
  )

  // Convert AI messages to ChatMessageData format (filter out system messages)
  const messages: ChatMessageData[] = useMemo(() => {
    return aiMessages
      .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
      .map((msg) => ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        createdAt: new Date(msg.created_at),
      }))
  }, [aiMessages])

  const [isEnhancing, setIsEnhancing] = useState(false)
  const [isEnhanceModalOpen, setIsEnhanceModalOpen] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  // Project data from hook
  const projectName = project?.name ?? ''
  const currentVersion = project?.current_version ?? 1

  // Helper: Extract section content from markdown
  const extractSectionContent = useCallback(
    (sectionTitle: string): string => {
      // Match ## Title and capture everything until next ## or end
      const escapedTitle = sectionTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(
        `## ${escapedTitle}\\s*\\n([\\s\\S]*?)(?=\\n## |$)`,
        'i'
      )
      const match = specsContent.match(regex)
      return match ? match[1].trim() : ''
    },
    [specsContent]
  )

  // Helper: Calculate section status based on word count and structure
  const getSectionStatus = useCallback((content: string): SectionStatus => {
    if (!content || content.trim().length === 0) return 'empty'

    const wordCount = content.trim().split(/\s+/).length
    const hasTable = content.includes('|') && content.includes('\n|')
    const hasList = /^[-*•]\s/m.test(content) || /^\d+\.\s/m.test(content)
    const hasMermaid = content.includes('```mermaid')

    // Complete: >30 words OR has structured content (table/list/mermaid)
    if (wordCount > 30 || hasTable || hasMermaid) return 'complete'
    // Draft: 10-30 words or has list
    if (wordCount >= 10 || hasList) return 'draft'
    // Empty: <10 words
    return 'empty'
  }, [])

  // Helper: Get preview (first meaningful line, max 80 chars)
  const getPreview = useCallback((content: string): string => {
    if (!content) return ''
    // Find first non-empty, non-header line
    const lines = content.split('\n')
    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed && !trimmed.startsWith('#') && !trimmed.startsWith('|')) {
        // Remove markdown formatting
        const clean = trimmed.replace(/[*_`]/g, '').substring(0, 80)
        return clean.length < trimmed.length ? `${clean}...` : clean
      }
    }
    return ''
  }, [])

  // Calculate section data with status, content, preview, wordCount
  const specSections: SpecSection[] = useMemo(() => {
    // Section title patterns for extraction
    const sectionTitles: Record<string, string> = {
      summary: t('scoping.sections.summary'),
      context: t('scoping.sections.context'),
      users: t('scoping.sections.users'),
      features: t('scoping.sections.features'),
      workflows: t('scoping.sections.workflows'),
      architecture: t('scoping.sections.architecture'),
      constraints: t('scoping.sections.constraints'),
      risks: t('scoping.sections.risks'),
      successCriteria: t('scoping.sections.successCriteria'),
    }

    return SPEC_SECTION_KEYS.map((key) => {
      const title = sectionTitles[key]
      const content = extractSectionContent(title)
      const wordCount = content ? content.trim().split(/\s+/).length : 0

      return {
        key,
        status: getSectionStatus(content),
        content,
        preview: getPreview(content),
        wordCount,
      }
    })
  }, [specsContent, t, extractSectionContent, getSectionStatus, getPreview])

  const handleBack = useCallback(() => {
    navigate('/dashboard')
  }, [navigate])

  // Enhancement count from project data
  const enhancementCount = project?.enhancement_count ?? 0
  const canEnhance = enhancementCount < MAX_ENHANCEMENTS

  // Open enhancement modal (with limit check)
  const handleEnhanceClick = useCallback(() => {
    if (!canEnhance) {
      toast.error(t('scopingV2.enhance.limitReached'))
      return
    }
    setIsEnhanceModalOpen(true)
  }, [canEnhance, t])

  // Close enhancement modal
  const handleCloseEnhanceModal = useCallback(() => {
    if (!isEnhancing) {
      setIsEnhanceModalOpen(false)
    }
  }, [isEnhancing])

  // Confirm and perform enhancement
  const handleConfirmEnhance = useCallback(async () => {
    if (isEnhancing || !project?.id || !specsContent) return

    setIsEnhancing(true)

    try {
      // Call the ai-enhance-specs Edge Function
      const response = await supabase.functions.invoke('ai-enhance-specs', {
        body: {
          projectId: project.id,
          currentSpecs: specsContent,
          projectContext: projectContext,
          language: 'fr', // TODO: Get from i18n
        },
      })

      if (response.error) {
        throw new Error(response.error.message || 'Enhancement failed')
      }

      const { enhancedSpecs, summary } = response.data

      if (!enhancedSpecs) {
        throw new Error('No enhanced specs returned')
      }

      // Update specs in state and ref
      specsContentRef.current = enhancedSpecs
      setSpecsContent(enhancedSpecs)

      // Persist enhanced specs and increment counter
      const { error: updateError } = await supabase
        .from('projects')
        .update({
          specs_content: enhancedSpecs,
          specs_updated_at: new Date().toISOString(),
          enhancement_count: enhancementCount + 1,
        })
        .eq('id', project.id)

      if (updateError) {
        throw new Error('Failed to save enhanced specs')
      }

      // Invalidate project query to refresh data
      queryClient.invalidateQueries({ queryKey: ['project', 'short', shortId] })

      // Close modal and show success
      setIsEnhanceModalOpen(false)
      toast.success(
        t('scopingV2.enhance.success') +
          (summary?.diagramsAdded ? ` (+${summary.diagramsAdded} diagrams)` : '')
      )
    } catch (error) {
      console.error('Failed to enhance specs:', error)
      toast.error(t('scopingV2.enhance.error'))
    } finally {
      setIsEnhancing(false)
    }
  }, [isEnhancing, project?.id, specsContent, projectContext, enhancementCount, shortId, t, queryClient])

  const handlePreviewSpecs = useCallback(() => {
    setIsPreviewOpen(true)
  }, [])

  const handleClosePreview = useCallback(() => {
    setIsPreviewOpen(false)
  }, [])

  const handleGeneratePdf = useCallback(async () => {
    if (!shortId || !project?.id) return

    setIsGeneratingPdf(true)

    try {
      // Call Edge Function to generate PDF, upload to storage, and update DB
      const { data, error } = await supabase.functions.invoke<{
        pdfUrl: string
        storagePath: string
        size: number
      }>('generate-specs-pdf', {
        body: {
          projectId: project.id,
          specsContent,
          projectName: project.name,
          versionNumber: project.current_version || 1,
          language: i18n.language.startsWith('fr') ? 'fr' : 'en',
        },
      })

      if (error) {
        console.error('Edge Function error:', error)
        throw new Error(error.message || 'PDF generation failed')
      }

      if (!data?.pdfUrl) {
        throw new Error('No PDF URL returned')
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['project', 'short', shortId] })

      // Show success toast
      toast.success(t('scopingV2.generate.success'))

      // Close the preview modal
      setIsPreviewOpen(false)

      // Navigate to post-scoping page
      navigate(`/scoping/${shortId}/complete`)
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      toast.error(t('scopingV2.generate.error'))
    } finally {
      setIsGeneratingPdf(false)
    }
  }, [shortId, project?.id, project?.name, project?.current_version, specsContent, navigate, t, i18n.language, queryClient])

  const handleSendMessage = useCallback((content: string) => {
    sendMessage(content)
  }, [sendMessage])

  // Header action buttons (icon-only with tooltips)
  const enhanceTooltip = canEnhance
    ? `${t('scopingV2.header.enhanceTooltip')} (${enhancementCount}/${MAX_ENHANCEMENTS})`
    : t('scopingV2.enhance.limitReached')

  const headerActions = (
    <>
      {/* Enhance specs button (blue, icon-only) */}
      <button
        onClick={handleEnhanceClick}
        disabled={isEnhancing || !canEnhance}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500 text-white transition-colors sm:h-9 sm:w-9',
          isEnhancing || !canEnhance
            ? 'cursor-not-allowed opacity-50'
            : 'hover:bg-blue-600'
        )}
        title={enhanceTooltip}
        aria-label={enhanceTooltip}
      >
        {isEnhancing ? (
          <Loader2 className="h-4 w-4 animate-spin sm:h-[18px] sm:w-[18px]" />
        ) : (
          <Wand2 className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
        )}
      </button>

      {/* Preview specs button (outline, icon-only) */}
      <button
        onClick={handlePreviewSpecs}
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-background-secondary sm:h-9 sm:w-9"
        title={t('scopingV2.header.previewTooltip')}
        aria-label={t('scopingV2.header.previewTooltip')}
      >
        <FileText className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
      </button>
    </>
  )

  // Loading state
  if (isLoadingProject) {
    return (
      <div className="flex h-dvh items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
      </div>
    )
  }

  // Error state
  if (projectError || !project) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4 bg-background">
        <p className="text-foreground-muted">{t('scopingV2.error.notFound')}</p>
        <button
          onClick={handleBack}
          className="text-accent hover:underline"
        >
          {t('scopingV2.error.backToDashboard')}
        </button>
      </div>
    )
  }

  // Show AI error if any
  if (aiError) {
    toast.error(aiError)
  }

  return (
    <div className="flex h-dvh flex-col bg-background">
      {/* Header with contextual navigation */}
      <ScopingHeader
        projectName={projectName}
        version={currentVersion}
        onBack={handleBack}
        actions={headerActions}
      />

      {/* Main chat area - full width, no sidebar */}
      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Messages zone */}
        <ChatMessages messages={messages} isLoading={isAiLoading} />

        {/* Input area */}
        <ChatInput onSend={handleSendMessage} isLoading={isAiLoading} />
      </main>

      {/* Specs Preview Modal */}
      <SpecsPreviewModal
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        sections={specSections}
        onGeneratePdf={handleGeneratePdf}
        isGenerating={isGeneratingPdf}
        shortId={shortId}
      />

      {/* Enhancement Confirmation Modal */}
      <ConfirmModal
        isOpen={isEnhanceModalOpen}
        onClose={handleCloseEnhanceModal}
        onConfirm={handleConfirmEnhance}
        isLoading={isEnhancing}
        title={t('scopingV2.enhance.modalTitle')}
        description={`${t('scopingV2.enhance.modalDescription')}\n\n${t('scopingV2.enhance.counter', { used: enhancementCount, max: MAX_ENHANCEMENTS })}`}
        confirmLabel={t('scopingV2.enhance.confirm')}
      />
    </div>
  )
}
